import type { LineExportMessage } from './lineExportParser'

export type LineInsightTopic =
  | 'onboarding'
  | 'schedule_call'
  | 'payment_tax'
  | 'event'
  | 'rights_rules'
  | 'motivation'

export type LineRiskCategory =
  | 'payment_tax'
  | 'follow_up_pressure'
  | 'slow_response'
  | 'long_no_contact'
  | 'rights_rules'

export type ManagerCandidate = {
  name: string
  messageCount: number
  textCount: number
  textChars: number
  activeDays: number
  callStarts: number
  share: number
  averageChars: number
  responseMedianHours: number | null
  responseWithin24hRate: number | null
  styleTags: string[]
}

export type HandoffSignal = {
  date: string
  actor: string
  added: string
  reason: 'group_add' | 'monthly_top_changed'
}

export type TopicInsight = {
  topic: LineInsightTopic
  count: number
  days: number
  senders: Record<string, number>
}

export type RiskInsight = {
  category: LineRiskCategory
  severity: 'low' | 'medium' | 'high'
  date: string
  sender: string
  summary: string
}

export type LineInsightsResult = {
  messageCount: number
  activeDays: number
  dateRange: { start: string | null; end: string | null }
  monthlyCounts: Record<string, number>
  longestNoContactDays: number
  primaryManager: ManagerCandidate | null
  managerCandidates: ManagerCandidate[]
  handoffSignals: HandoffSignal[]
  topics: TopicInsight[]
  risks: RiskInsight[]
  callEvents: {
    total: number
    byStarter: Record<string, number>
  }
  handoffNote: string
}

export type AnalyzeLineExportOptions = {
  liverName?: string | null
}

const BOT_NAME_PATTERNS = [/業務連絡/, /Linear_17V/i, /bot/i]

const TOPIC_KEYWORDS: Record<LineInsightTopic, string[]> = {
  onboarding: ['2D', 'プロフィール', 'Vプロフ', 'X', 'Twitter', 'OBS', 'VTube Studio', 'リス活', '宣伝', '初配信', '準備'],
  schedule_call: ['日程', '電話', 'おかけ', '通話', 'Timerex', '都合'],
  payment_tax: ['報酬', '支払調書', '確定申告', '明細', '送付', '支払', '換金', '振込', 'マイレベニュー'],
  event: ['イベント', 'イベ', 'pt', 'ポイント', '条件達成', 'ルーレット', '講習会', '進撃', 'プライズ', 'ギフチャレ'],
  rights_rules: ['イラスト', 'ファンアート', '権利', 'ルール', 'BAN', '規約', 'コラボ', '配信不可', '任天堂', '音源'],
  motivation: ['頑張', '応援', '楽し', '無理', '体調', '元気', 'ファイト']
}

export function analyzeLineExport(
  messages: LineExportMessage[],
  options: AnalyzeLineExportOptions = {}
): LineInsightsResult {
  const normalized = messages
    .map((message) => ({ ...message, body: normalizeBody(message.body) }))
    .filter((message) => message.date && message.time)
    .sort((a, b) => toTime(a).getTime() - toTime(b).getTime())

  const participantMessages = normalized.filter((message) => message.sender.trim())
  const activeDays = new Set(participantMessages.map((message) => message.date))
  const monthlyCounts = countBy(participantMessages, (message) => message.date.slice(0, 7))
  const liverName = options.liverName?.trim() || inferLiverName(participantMessages)
  const managerNames = inferManagerNames(participantMessages, liverName)
  const managerMessages = participantMessages.filter((message) => managerNames.has(message.sender))
  const managerTotal = managerMessages.length
  const responseTimes = computeQuestionResponseTimes(normalized, liverName, managerNames)
  const managerCandidates = [...managerNames]
    .map((name) => buildManagerCandidate(name, managerMessages, responseTimes, managerTotal))
    .sort((a, b) => b.messageCount - a.messageCount || b.callStarts - a.callStarts)

  const handoffSignals = [
    ...detectGroupAddSignals(normalized, managerNames),
    ...detectMonthlyTopChanges(managerMessages, managerNames)
  ]
  const risks = detectRisks(normalized, liverName, responseTimes)
  const topics = buildTopicInsights(normalized)
  const callStarts = participantMessages.filter((message) => message.message_type === 'call_start')
  const byStarter = countBy(callStarts, (message) => message.sender)
  const dateRange = {
    start: participantMessages[0]?.date ?? null,
    end: participantMessages.at(-1)?.date ?? null
  }
  const longestNoContactDays = computeLongestNoContactDays([...activeDays].sort())

  return {
    messageCount: participantMessages.length,
    activeDays: activeDays.size,
    dateRange,
    monthlyCounts,
    longestNoContactDays,
    primaryManager: managerCandidates[0] ?? null,
    managerCandidates,
    handoffSignals,
    topics,
    risks,
    callEvents: {
      total: callStarts.length,
      byStarter
    },
    handoffNote: buildHandoffNote({
      liverName,
      primaryManager: managerCandidates[0] ?? null,
      managerCandidates,
      handoffSignals,
      risks,
      topics,
      longestNoContactDays
    })
  }
}

function buildManagerCandidate(
  name: string,
  managerMessages: LineExportMessage[],
  responseTimes: Array<{ manager: string; hours: number }>,
  managerTotal: number
): ManagerCandidate {
  const own = managerMessages.filter((message) => message.sender === name)
  const textMessages = own.filter((message) => message.message_type === 'text')
  const textChars = textMessages.reduce((sum, message) => sum + message.body.length, 0)
  const callStarts = own.filter((message) => message.message_type === 'call_start').length
  const activeDays = new Set(own.map((message) => message.date)).size
  const responses = responseTimes
    .filter((response) => response.manager === name)
    .map((response) => response.hours)
  const responseMedianHours = median(responses)
  const responseWithin24hRate =
    responses.length > 0 ? responses.filter((hours) => hours <= 24).length / responses.length : null

  return {
    name,
    messageCount: own.length,
    textCount: textMessages.length,
    textChars,
    activeDays,
    callStarts,
    share: managerTotal > 0 ? own.length / managerTotal : 0,
    averageChars: textMessages.length > 0 ? textChars / textMessages.length : 0,
    responseMedianHours,
    responseWithin24hRate,
    styleTags: buildStyleTags({ own, textMessages, responseMedianHours, callStarts })
  }
}

function buildStyleTags({
  own,
  textMessages,
  responseMedianHours,
  callStarts
}: {
  own: LineExportMessage[]
  textMessages: LineExportMessage[]
  responseMedianHours: number | null
  callStarts: number
}): string[] {
  const tags: string[] = []
  const bodies = textMessages.map((message) => message.body).join('\n')
  const mediaOrUrls = own.filter((message) => message.message_type !== 'text' || /https?:\/\//.test(message.body)).length
  if (responseMedianHours !== null && responseMedianHours <= 1) tags.push('即レス型')
  if (callStarts >= 2 || callStarts / Math.max(own.length, 1) >= 0.05) tags.push('電話説明型')
  if (mediaOrUrls >= 3) tags.push('資料共有型')
  if (/いいですね|バッチリ|頑張|ファイト|おめでとう/.test(bodies)) tags.push('肯定フィードバック型')
  if (/報酬|支払|確定申告|明細/.test(bodies)) tags.push('事務対応型')
  return tags.length > 0 ? tags : ['通常対応型']
}

function inferLiverName(messages: LineExportMessage[]): string | null {
  const names = countBy(
    messages.filter((message) => !isSystemLikeSender(message.sender)),
    (message) => message.sender
  )
  return Object.entries(names).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null
}

function inferManagerNames(messages: LineExportMessage[], liverName: string | null): Set<string> {
  const names = new Set<string>()
  for (const message of messages) {
    const sender = message.sender.trim()
    if (!sender || sender === liverName || isSystemLikeSender(sender)) continue
    names.add(sender)
  }
  return names
}

function isSystemLikeSender(sender: string): boolean {
  return BOT_NAME_PATTERNS.some((pattern) => pattern.test(sender))
}

function computeQuestionResponseTimes(
  messages: LineExportMessage[],
  liverName: string | null,
  managerNames: Set<string>
): Array<{ date: string; manager: string; hours: number; question: string }> {
  if (!liverName) return []
  const responses: Array<{ date: string; manager: string; hours: number; question: string }> = []
  for (const [index, message] of messages.entries()) {
    if (message.sender !== liverName || message.message_type !== 'text' || !isQuestion(message.body)) continue
    for (const next of messages.slice(index + 1)) {
      if (!managerNames.has(next.sender) || next.message_type !== 'text') continue
      const hours = (toTime(next).getTime() - toTime(message).getTime()) / 36e5
      if (hours >= 0) {
        responses.push({ date: message.date, manager: next.sender, hours, question: message.body })
      }
      break
    }
  }
  return responses
}

function detectGroupAddSignals(
  messages: LineExportMessage[],
  managerNames: Set<string>
): HandoffSignal[] {
  const signals: HandoffSignal[] = []
  for (const message of messages) {
    const text = stripLineControls(message.body)
    const match = /^(.+?)が(.+?)をグループに追加しました。?$/.exec(text)
    if (!match) continue
    const actor = match[1].trim()
    const added = match[2].trim()
    if (!actor || !added) continue
    if (managerNames.has(added)) {
      signals.push({ date: message.date, actor, added, reason: 'group_add' })
    }
  }
  return signals
}

function detectMonthlyTopChanges(
  managerMessages: LineExportMessage[],
  managerNames: Set<string>
): HandoffSignal[] {
  const byMonth = new Map<string, Record<string, number>>()
  for (const message of managerMessages) {
    if (!managerNames.has(message.sender)) continue
    const month = message.date.slice(0, 7)
    const monthCounts = byMonth.get(month) ?? {}
    monthCounts[message.sender] = (monthCounts[message.sender] ?? 0) + 1
    byMonth.set(month, monthCounts)
  }

  const signals: HandoffSignal[] = []
  let previousTop: string | null = null
  for (const [month, counts] of [...byMonth.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null
    if (top && previousTop && top !== previousTop) {
      signals.push({
        date: `${month}-01`,
        actor: previousTop,
        added: top,
        reason: 'monthly_top_changed'
      })
    }
    if (top) previousTop = top
  }
  return signals
}

function detectRisks(
  messages: LineExportMessage[],
  liverName: string | null,
  responseTimes: Array<{ date: string; manager: string; hours: number; question: string }>
): RiskInsight[] {
  const risks: RiskInsight[] = []
  const activeDays = [...new Set(messages.filter((message) => message.sender).map((message) => message.date))].sort()
  const longestGap = computeLongestNoContactDays(activeDays)
  if (activeDays.length > 1 && longestGap >= 60) {
    risks.push({
      category: 'long_no_contact',
      severity: longestGap >= 120 ? 'high' : 'medium',
      date: activeDays.at(-1) ?? '',
      sender: '',
      summary: `${longestGap}日以上の接触空白があります`
    })
  }

  for (const message of messages) {
    const body = message.body
    if (/(支払調書|確定申告|報酬|明細|振込|マイレベニュー)/.test(body)) {
      risks.push({
        category: 'payment_tax',
        severity: /(再三|どうなって|間に合わ|3\/16|確定申告)/.test(body) ? 'high' : 'medium',
        date: message.date,
        sender: message.sender,
        summary: summarize(body)
      })
    }
    if (/(再三|どうなって|急かす|送付予定日|間に合わ|既に.+過ぎ)/.test(body)) {
      risks.push({
        category: 'follow_up_pressure',
        severity: 'high',
        date: message.date,
        sender: message.sender,
        summary: summarize(body)
      })
    }
    if (/(権利|コラボ|ファンアート|配信不可|任天堂|音源|ルール)/.test(body)) {
      risks.push({
        category: 'rights_rules',
        severity: 'medium',
        date: message.date,
        sender: message.sender,
        summary: summarize(body)
      })
    }
  }

  for (const response of responseTimes) {
    if (response.hours >= 48) {
      risks.push({
        category: 'slow_response',
        severity: response.hours >= 96 ? 'high' : 'medium',
        date: response.date,
        sender: liverName ?? '',
        summary: `質問への返信が約${Math.round(response.hours)}時間後です: ${summarize(response.question)}`
      })
    }
  }

  return dedupeRisks(risks).slice(0, 12)
}

function buildTopicInsights(messages: LineExportMessage[]): TopicInsight[] {
  return (Object.entries(TOPIC_KEYWORDS) as Array<[LineInsightTopic, string[]]>)
    .map(([topic, keywords]) => {
      const hits = messages.filter((message) =>
        keywords.some((keyword) => message.body.toLowerCase().includes(keyword.toLowerCase()))
      )
      return {
        topic,
        count: hits.length,
        days: new Set(hits.map((message) => message.date)).size,
        senders: countBy(hits, (message) => message.sender || '(system)')
      }
    })
    .filter((topic) => topic.count > 0)
    .sort((a, b) => b.count - a.count)
}

function buildHandoffNote(args: {
  liverName: string | null
  primaryManager: ManagerCandidate | null
  managerCandidates: ManagerCandidate[]
  handoffSignals: HandoffSignal[]
  risks: RiskInsight[]
  topics: TopicInsight[]
  longestNoContactDays: number
}): string {
  const lines: string[] = []
  lines.push(`LINE履歴分析: ${args.liverName ?? '対象ライバー'}`)
  if (args.primaryManager) {
    lines.push(
      `メイン担当候補: ${args.primaryManager.name} (${args.primaryManager.messageCount}件 / 担当候補内 ${(args.primaryManager.share * 100).toFixed(1)}%)`
    )
  }
  if (args.managerCandidates.length > 1) {
    lines.push(`担当候補: ${args.managerCandidates.map((manager) => `${manager.name} ${manager.messageCount}件`).join('、')}`)
  }
  const handoff = args.handoffSignals[0]
  if (handoff) {
    lines.push(`担当交代シグナル: ${handoff.date} ${handoff.actor} -> ${handoff.added} (${handoff.reason})`)
  }
  const topTopics = args.topics.slice(0, 3).map((topic) => topicLabel(topic.topic)).join(' / ')
  if (topTopics) lines.push(`主な話題: ${topTopics}`)
  if (args.longestNoContactDays >= 30) {
    lines.push(`接触空白: 最大${args.longestNoContactDays}日`)
  }
  const riskLabels = [...new Set(args.risks.map((risk) => riskLabel(risk.category)))].slice(0, 4)
  if (riskLabels.length > 0) lines.push(`注意点: ${riskLabels.join(' / ')}`)
  return lines.join('\n')
}

function topicLabel(topic: LineInsightTopic): string {
  const labels: Record<LineInsightTopic, string> = {
    onboarding: 'オンボーディング',
    schedule_call: '日程・通話',
    payment_tax: '支払・税務',
    event: 'イベント',
    rights_rules: '権利・ルール',
    motivation: 'モチベーション'
  }
  return labels[topic]
}

export function lineTopicLabel(topic: LineInsightTopic): string {
  return topicLabel(topic)
}

function riskLabel(category: LineRiskCategory): string {
  const labels: Record<LineRiskCategory, string> = {
    payment_tax: '支払・税務',
    follow_up_pressure: '催促・心理負担',
    slow_response: '返信遅延',
    long_no_contact: '長期未接触',
    rights_rules: '権利・ルール確認'
  }
  return labels[category]
}

export function lineRiskLabel(category: LineRiskCategory): string {
  return riskLabel(category)
}

function isQuestion(body: string): boolean {
  return /[?？]|でしょうか|ですか|ますか|教えて|確認/.test(body)
}

function computeLongestNoContactDays(sortedDates: string[]): number {
  let longest = 0
  for (let i = 1; i < sortedDates.length; i += 1) {
    const current = new Date(`${sortedDates[i]}T00:00:00`).getTime()
    const previous = new Date(`${sortedDates[i - 1]}T00:00:00`).getTime()
    longest = Math.max(longest, Math.round((current - previous) / 86400000))
  }
  return longest
}

function countBy<T>(items: T[], keyFn: (item: T) => string): Record<string, number> {
  return items.reduce<Record<string, number>>((acc, item) => {
    const key = keyFn(item)
    acc[key] = (acc[key] ?? 0) + 1
    return acc
  }, {})
}

function median(values: number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle]
}

function toTime(message: Pick<LineExportMessage, 'date' | 'time'>): Date {
  return new Date(`${message.date}T${message.time}:00`)
}

function normalizeBody(body: string): string {
  const text = body.trim()
  return text.startsWith('"') && text.endsWith('"') ? text.slice(1, -1).trim() : text
}

function stripLineControls(value: string): string {
  return value.replace(/[\u2066-\u2069]/g, '').replace(/\s+/g, '').trim()
}

function summarize(body: string): string {
  const text = body.replace(/\s+/g, ' ').trim()
  return text.length > 80 ? `${text.slice(0, 80)}...` : text
}

function dedupeRisks(risks: RiskInsight[]): RiskInsight[] {
  const seen = new Set<string>()
  const result: RiskInsight[] = []
  for (const risk of risks.sort((a, b) => severityRank(b.severity) - severityRank(a.severity))) {
    const key = `${risk.category}:${risk.date}:${risk.summary}`
    if (seen.has(key)) continue
    seen.add(key)
    result.push(risk)
  }
  return result
}

function severityRank(severity: RiskInsight['severity']): number {
  return severity === 'high' ? 3 : severity === 'medium' ? 2 : 1
}
